// src/lib/store.js
// In-memory workspace cache mirroring Supabase tables.
// v2.2: adds deal status, currency, quote fields, attachments slice.

import { db, loadWorkspace, uploadAttachment, deleteAttachmentFile, inviteUser as inviteUserModule, setUserPassword } from './supabase.js';

export const state = {
  companies: [],
  contacts: [],
  deals: [],
  tickets: [],
  activities: [],
  attachments: [],
  profiles: [],
  currentProfile: null,    // the logged-in user's profile (with is_admin / is_active)
  fxRates: { zarPerUsd: 0, zarPerEur: 0, asOfDate: null },
  settings: {
    currency: 'ZAR', // workspace default
    currencies: ['ZAR', 'USD', 'EUR'],
    displayCurrency: 'native', // dashboard toggle: 'native' | 'ZAR' | 'USD' | 'EUR'
    dealStages: [
      { id: 'lead',      name: 'Lead',        probability: 10 },
      { id: 'qualified', name: 'Qualified',   probability: 25 },
      { id: 'pilot',     name: 'Pilot',       probability: 50 },
      { id: 'proposal',  name: 'Proposal',    probability: 75 },
      { id: 'won',       name: 'Won',         probability: 100 }
    ],
    dealStatuses: [
      { id: 'active',    name: 'Active',    color: '' },
      { id: 'blocked',   name: 'Blocked',   color: 'warn' },
      { id: 'stopped',   name: 'Stopped',   color: 'muted' },
      { id: 'cancelled', name: 'Cancelled', color: 'bad' }
    ],
    ticketStages: [
      { id: 'new',     name: 'New' },
      { id: 'working', name: 'Working on it' },
      { id: 'waiting', name: 'Waiting on customer' },
      { id: 'closed',  name: 'Closed' }
    ],
    segments: ['Enterprise','Mid-market','Small buyer','Pilot customer','Distributor'],
    industries: ['Manufacturing','Mining','Energy','Agriculture','Logistics','Research','Other'],
    priorities: ['low','medium','high']
  },
  session: null,
  loading: false,
  error: null
};

// Persist workspace settings (currency default + display currency) in localStorage
const SETTINGS_KEY = 'igv_crm_v22_settings';
try {
  const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) || 'null');
  if (saved && saved.currency) state.settings.currency = saved.currency;
  if (saved && saved.displayCurrency) state.settings.displayCurrency = saved.displayCurrency;
} catch (_) { /* ignore */ }
export function persistSettings() {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify({
      currency: state.settings.currency,
      displayCurrency: state.settings.displayCurrency
    }));
  } catch (_) { /* ignore */ }
}

// -------------------------------------------------------------
// Field mapping (DB snake_case <-> app camelCase)
// -------------------------------------------------------------
function mapCompanyFromDb(r) {
  return {
    id: r.id, name: r.name, industry: r.industry, segment: r.segment,
    website: r.website, phone: r.phone, address: r.address, notes: r.notes,
    createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now()
  };
}
function mapCompanyToDb(r) {
  return {
    name: r.name || '', industry: r.industry || null, segment: r.segment || null,
    website: r.website || null, phone: r.phone || null,
    address: r.address || null, notes: r.notes || null
  };
}
function mapContactFromDb(r) {
  return {
    id: r.id, firstName: r.first_name, lastName: r.last_name, title: r.title,
    email: r.email, phone: r.phone, lifecycle: r.lifecycle, notes: r.notes,
    companyId: r.company_id,
    createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now()
  };
}
function mapContactToDb(r) {
  return {
    first_name: r.firstName || null, last_name: r.lastName || null,
    title: r.title || null, email: r.email || null, phone: r.phone || null,
    lifecycle: r.lifecycle || null, notes: r.notes || null,
    company_id: r.companyId || null
  };
}
function mapDealFromDb(r) {
  return {
    id: r.id, title: r.title,
    value: r.value != null ? Number(r.value) : '',
    stage: r.stage,
    status: r.status || 'active',
    currency: r.currency || null,
    priority: r.priority,
    expectedClose: r.expected_close, notes: r.notes,
    quoteNumber: r.quote_number || '',
    quoteDate: r.quote_date || '',
    quoteAttachmentId: r.quote_attachment_id || null,
    companyId: r.company_id, contactId: r.contact_id,
    createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now()
  };
}
function mapDealToDb(r) {
  const out = {
    title: r.title,
    value: r.value === '' || r.value == null ? null : Number(r.value),
    stage: r.stage || 'lead',
    status: r.status || 'active',
    currency: r.currency || null,
    priority: r.priority || null,
    expected_close: r.expectedClose || null,
    notes: r.notes || null,
    quote_number: r.quoteNumber || null,
    quote_date: r.quoteDate || null,
    company_id: r.companyId || null,
    contact_id: r.contactId || null
  };
  // Only include quote_attachment_id when explicitly set (allows null clears)
  if ('quoteAttachmentId' in r) out.quote_attachment_id = r.quoteAttachmentId || null;
  return out;
}
function mapTicketFromDb(r) {
  return {
    id: r.id, subject: r.subject, description: r.description,
    stage: r.stage, priority: r.priority,
    companyId: r.company_id, contactId: r.contact_id,
    createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now()
  };
}
function mapTicketToDb(r) {
  return {
    subject: r.subject, description: r.description || null,
    stage: r.stage || 'new', priority: r.priority || null,
    company_id: r.companyId || null, contact_id: r.contactId || null
  };
}
function mapActivityFromDb(r) {
  return {
    id: r.id, type: r.type, subject: r.subject, notes: r.notes,
    date: r.activity_date, done: !!r.done,
    companyId: r.company_id, contactId: r.contact_id,
    dealId: r.deal_id, ticketId: r.ticket_id,
    createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now()
  };
}
function mapActivityToDb(r) {
  return {
    type: r.type || 'note', subject: r.subject || null, notes: r.notes || null,
    activity_date: r.date || null, done: !!r.done,
    company_id: r.companyId || null, contact_id: r.contactId || null,
    deal_id: r.dealId || null, ticket_id: r.ticketId || null
  };
}
function mapAttachmentFromDb(r) {
  return {
    id: r.id,
    fileName: r.file_name,
    fileSize: r.file_size,
    mimeType: r.mime_type,
    storagePath: r.storage_path,
    companyId: r.company_id,
    dealId: r.deal_id,
    ticketId: r.ticket_id,
    createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now()
  };
}
function mapProfileFromDb(r) {
  return {
    id: r.id,
    email: r.email,
    fullName: r.full_name || '',
    isAdmin: !!r.is_admin,
    isActive: !!r.is_active,
    requiresPasswordSetup: !!r.requires_password_setup,
    createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now()
  };
}
function mapFxFromDb(row) {
  if (!row || !row.value) return { zarPerUsd: 0, zarPerEur: 0, asOfDate: null };
  return {
    zarPerUsd: Number(row.value.zar_per_usd) || 0,
    zarPerEur: Number(row.value.zar_per_eur) || 0,
    asOfDate: row.value.as_of_date || null
  };
}

// -------------------------------------------------------------
// Load all data
// -------------------------------------------------------------
export async function refreshAll() {
  state.loading = true;
  try {
    const ws = await loadWorkspace();
    state.companies   = ws.companies.map(mapCompanyFromDb);
    state.contacts    = ws.contacts.map(mapContactFromDb);
    state.deals       = ws.deals.map(mapDealFromDb);
    state.tickets     = ws.tickets.map(mapTicketFromDb);
    state.activities  = ws.activities.map(mapActivityFromDb);
    state.attachments = ws.attachments.map(mapAttachmentFromDb);
    state.profiles    = (ws.profiles || []).map(mapProfileFromDb);
    state.fxRates     = mapFxFromDb(ws.fxSettings);
    // Identify current user's profile (used for admin gating)
    const uid = state.session && state.session.user ? state.session.user.id : null;
    state.currentProfile = uid ? state.profiles.find(p => p.id === uid) || null : null;
    state.error = null;
  } catch (e) {
    console.error('refreshAll failed', e);
    state.error = e.message || 'Failed to load data';
    throw e;
  } finally {
    state.loading = false;
  }
}

// -------------------------------------------------------------
// Per-entity mutation wrappers
// -------------------------------------------------------------
export const store = {
  companies: {
    async create(data) {
      const row = await db.companies.create(mapCompanyToDb(data));
      state.companies.unshift(mapCompanyFromDb(row));
      state.companies.sort((a,b) => (a.name||'').localeCompare(b.name||''));
      return row.id;
    },
    async update(id, patch) {
      const row = await db.companies.update(id, mapCompanyToDb(patch));
      const i = state.companies.findIndex(c => c.id === id);
      if (i >= 0) state.companies[i] = mapCompanyFromDb(row);
    },
    async remove(id) {
      await db.companies.remove(id);
      state.companies = state.companies.filter(c => c.id !== id);
      state.contacts.forEach(c => { if (c.companyId === id) c.companyId = null; });
      state.deals.forEach(d => { if (d.companyId === id) d.companyId = null; });
      state.tickets.forEach(t => { if (t.companyId === id) t.companyId = null; });
      state.activities.forEach(a => { if (a.companyId === id) a.companyId = null; });
      state.attachments = state.attachments.filter(a => a.companyId !== id);
    }
  },
  contacts: {
    async create(data) {
      const row = await db.contacts.create(mapContactToDb(data));
      state.contacts.unshift(mapContactFromDb(row));
      return row.id;
    },
    async update(id, patch) {
      const row = await db.contacts.update(id, mapContactToDb(patch));
      const i = state.contacts.findIndex(c => c.id === id);
      if (i >= 0) state.contacts[i] = mapContactFromDb(row);
    },
    async remove(id) {
      await db.contacts.remove(id);
      state.contacts = state.contacts.filter(c => c.id !== id);
      state.deals.forEach(d => { if (d.contactId === id) d.contactId = null; });
      state.tickets.forEach(t => { if (t.contactId === id) t.contactId = null; });
      state.activities.forEach(a => { if (a.contactId === id) a.contactId = null; });
    }
  },
  deals: {
    async create(data) {
      const row = await db.deals.create(mapDealToDb(data));
      state.deals.unshift(mapDealFromDb(row));
      return row.id;
    },
    async update(id, patch) {
      const row = await db.deals.update(id, mapDealToDb(patch));
      const i = state.deals.findIndex(d => d.id === id);
      if (i >= 0) state.deals[i] = mapDealFromDb(row);
    },
    async updateStage(id, stage) {
      const row = await db.deals.update(id, { stage });
      const i = state.deals.findIndex(d => d.id === id);
      if (i >= 0) state.deals[i] = mapDealFromDb(row);
    },
    async updateStatus(id, status) {
      const row = await db.deals.update(id, { status });
      const i = state.deals.findIndex(d => d.id === id);
      if (i >= 0) state.deals[i] = mapDealFromDb(row);
    },
    async setQuoteAttachment(id, attachmentId) {
      const row = await db.deals.update(id, { quote_attachment_id: attachmentId || null });
      const i = state.deals.findIndex(d => d.id === id);
      if (i >= 0) state.deals[i] = mapDealFromDb(row);
    },
    async remove(id) {
      await db.deals.remove(id);
      state.deals = state.deals.filter(d => d.id !== id);
      state.activities.forEach(a => { if (a.dealId === id) a.dealId = null; });
      state.attachments = state.attachments.filter(a => a.dealId !== id);
    }
  },
  tickets: {
    async create(data) {
      const row = await db.tickets.create(mapTicketToDb(data));
      state.tickets.unshift(mapTicketFromDb(row));
      return row.id;
    },
    async update(id, patch) {
      const row = await db.tickets.update(id, mapTicketToDb(patch));
      const i = state.tickets.findIndex(t => t.id === id);
      if (i >= 0) state.tickets[i] = mapTicketFromDb(row);
    },
    async updateStage(id, stage) {
      const row = await db.tickets.update(id, { stage });
      const i = state.tickets.findIndex(t => t.id === id);
      if (i >= 0) state.tickets[i] = mapTicketFromDb(row);
    },
    async remove(id) {
      await db.tickets.remove(id);
      state.tickets = state.tickets.filter(t => t.id !== id);
      state.activities.forEach(a => { if (a.ticketId === id) a.ticketId = null; });
      state.attachments = state.attachments.filter(a => a.ticketId !== id);
    }
  },
  activities: {
    async create(data) {
      const row = await db.activities.create(mapActivityToDb(data));
      state.activities.unshift(mapActivityFromDb(row));
      return row.id;
    },
    async update(id, patch) {
      const row = await db.activities.update(id, mapActivityToDb(patch));
      const i = state.activities.findIndex(a => a.id === id);
      if (i >= 0) state.activities[i] = mapActivityFromDb(row);
    },
    async toggleDone(id) {
      const a = state.activities.find(x => x.id === id);
      if (!a) return;
      const row = await db.activities.update(id, { done: !a.done });
      const i = state.activities.findIndex(x => x.id === id);
      if (i >= 0) state.activities[i] = mapActivityFromDb(row);
    },
    async remove(id) {
      await db.activities.remove(id);
      state.activities = state.activities.filter(a => a.id !== id);
    }
  },
  attachments: {
    async upload(file, parentKind, parentId) {
      const row = await uploadAttachment(file, parentKind, parentId);
      state.attachments.unshift(mapAttachmentFromDb(row));
      return mapAttachmentFromDb(row);
    },
    async remove(id) {
      const a = state.attachments.find(x => x.id === id);
      if (!a) return;
      await deleteAttachmentFile(id, a.storagePath);
      state.attachments = state.attachments.filter(x => x.id !== id);
      // Clear quote pointer on any deal that referenced this
      state.deals.forEach(d => { if (d.quoteAttachmentId === id) d.quoteAttachmentId = null; });
    }
  },
  profiles: {
    async setAdmin(id, makeAdmin) {
      const row = await db.profiles.update(id, { is_admin: !!makeAdmin });
      const i = state.profiles.findIndex(p => p.id === id);
      if (i >= 0) state.profiles[i] = mapProfileFromDb(row);
    },
    async setActive(id, makeActive) {
      const row = await db.profiles.update(id, { is_active: !!makeActive });
      const i = state.profiles.findIndex(p => p.id === id);
      if (i >= 0) state.profiles[i] = mapProfileFromDb(row);
    },
    async invite(email, fullName, makeAdmin) {
      const result = await inviteUserModule(email, fullName, makeAdmin);
      // The new profile row is created server-side by the trigger; refresh just the profiles list
      const fresh = await db.profiles.list();
      state.profiles = fresh.map(mapProfileFromDb);
      return result;
    },
    // Two-step flow used by the first-login password setup screen:
    //   1. Update the auth user's password
    //   2. Clear the requires_password_setup flag on their profile
    async completePasswordSetup(newPassword) {
      await setUserPassword(newPassword);
      const uid = state.session && state.session.user ? state.session.user.id : null;
      if (!uid) throw new Error('No session');
      const row = await db.profiles.update(uid, { requires_password_setup: false });
      const mapped = mapProfileFromDb(row);
      state.currentProfile = mapped;
      const i = state.profiles.findIndex(p => p.id === uid);
      if (i >= 0) state.profiles[i] = mapped;
    }
  },
  fxRates: {
    async save({ zarPerUsd, zarPerEur, asOfDate }) {
      const row = await db.settings.upsert('fx_rates', {
        zar_per_usd: Number(zarPerUsd) || 0,
        zar_per_eur: Number(zarPerEur) || 0,
        as_of_date: asOfDate || null
      });
      state.fxRates = mapFxFromDb(row);
      return state.fxRates;
    }
  }
};

// -------------------------------------------------------------
// Legacy localStorage import helper (unchanged)
// -------------------------------------------------------------
export async function importLegacyBackup(jsonText) {
  const parsed = JSON.parse(jsonText);
  if (!parsed.companies || !parsed.contacts) throw new Error('Not a valid IGV legacy backup');
  const idMap = {};
  for (const co of parsed.companies) {
    const row = await db.companies.create(mapCompanyToDb({
      name: co.name, industry: co.industry, segment: co.segment,
      website: co.website, phone: co.phone, address: co.address, notes: co.notes
    }));
    idMap[co.id] = row.id;
  }
  for (const c of parsed.contacts) {
    const row = await db.contacts.create(mapContactToDb({
      firstName: c.firstName, lastName: c.lastName, title: c.title,
      email: c.email, phone: c.phone, lifecycle: c.lifecycle, notes: c.notes,
      companyId: idMap[c.companyId] || null
    }));
    idMap[c.id] = row.id;
  }
  for (const d of parsed.deals || []) {
    const row = await db.deals.create(mapDealToDb({
      title: d.title, value: d.value, stage: d.stage, priority: d.priority,
      expectedClose: d.expectedClose, notes: d.notes,
      companyId: idMap[d.companyId] || null,
      contactId: idMap[d.contactId] || null
    }));
    idMap[d.id] = row.id;
  }
  for (const t of parsed.tickets || []) {
    const row = await db.tickets.create(mapTicketToDb({
      subject: t.subject, description: t.description,
      stage: t.stage, priority: t.priority,
      companyId: idMap[t.companyId] || null,
      contactId: idMap[t.contactId] || null
    }));
    idMap[t.id] = row.id;
  }
  for (const a of parsed.activities || []) {
    await db.activities.create(mapActivityToDb({
      type: a.type, subject: a.subject, notes: a.notes,
      date: a.date, done: a.done,
      companyId: idMap[a.companyId] || null,
      contactId: idMap[a.contactId] || null,
      dealId: idMap[a.dealId] || null,
      ticketId: idMap[a.ticketId] || null
    }));
  }
  return {
    companies: parsed.companies.length,
    contacts: parsed.contacts.length,
    deals: (parsed.deals||[]).length,
    tickets: (parsed.tickets||[]).length,
    activities: (parsed.activities||[]).length
  };
}
